import React, { useContext, useState, useEffect, useMemo } from "react";
import { SearchContext } from "./SearchContext";
import { useLocation, useNavigate } from "react-router-dom";
import { filterProviders } from "../utils/filterData";

const SupportFilterList = () => {
  const { supportList, originalData } = useContext(SearchContext);
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);

  // Get support term selection
  const currentSupport = searchParams.get("support_term");
  const selectedSupports = !currentSupport || currentSupport === "all" ? [] : currentSupport.split(",");

  // Get advanced search term selection
  const currentAdvSearch = searchParams.get("adv_search_term");
  const selectedAdvSearch = !currentAdvSearch || currentAdvSearch === "all" ? [] : currentAdvSearch.split(",");

  const [selectedFilters, setSelectedFilters] = useState(selectedSupports);

  useEffect(() => {
    const newParams = new URLSearchParams(location.search);
    if (selectedFilters.length > 0) {
      newParams.set("support_term", selectedFilters.join(","));
    } else {
      newParams.delete("support_term");
    }
    navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters]);

  // Compute baseline counts from originalData (no filters)
  const baselineAllCount = originalData.length;
  let baselineNoneCount = 0;
  const baselineSupportCounts = {};
  for (const item of originalData) {
    if (item.support_terms.length === 0) {
      baselineNoneCount++;
    }
    for (const term of item.support_terms) {
      if (!baselineSupportCounts[term.term_id]) {
        baselineSupportCounts[term.term_id] = 0;
      }
      baselineSupportCounts[term.term_id]++;
    }
  }

  // Compute advanced-only data and counts
  const advancedOnlyData = useMemo(() => {
    return filterProviders(originalData, [], selectedAdvSearch);
  }, [originalData, selectedAdvSearch]);

  let advancedNoneCount = 0;
  const advancedSupportCounts = {};
  for (const item of advancedOnlyData) {
    if (item.support_terms.length === 0) {
      advancedNoneCount++;
    }
    for (const term of item.support_terms) {
      if (!advancedSupportCounts[term.term_id]) {
        advancedSupportCounts[term.term_id] = 0;
      }
      advancedSupportCounts[term.term_id]++;
    }
  }

  // Check if advanced search is active
  const isAdvancedActive = selectedAdvSearch.length > 0;

  // Determine which counts to use
  // If no advanced filters, use baseline counts.
  // If advanced filters are active, use advancedOnlyData counts.
  const allCount = isAdvancedActive ? advancedOnlyData.length : baselineAllCount;
  const noneCount = isAdvancedActive ? advancedNoneCount : baselineNoneCount;
  const displayCounts = isAdvancedActive ? advancedSupportCounts : baselineSupportCounts;

  // Determine which terms appear at least once in originalData
  const originalTermIds = new Set(Object.keys(baselineSupportCounts).map(id => parseInt(id, 10)));

  const handleCheckboxChange = termId => {
    setSelectedFilters(prevSelected => {
      const cleaned = prevSelected.includes("none") ? [] : prevSelected;
      if (cleaned.includes(termId.toString())) {
        return cleaned.filter(id => id !== termId.toString());
      } else {
        return [...cleaned, termId.toString()];
      }
    });
  };

  const handleAllClick = () => {
    setSelectedFilters([]);
  };

  const handleNoneClick = () => {
    setSelectedFilters(["none"]);
  };

  const isChecked = termId => selectedFilters.includes(termId.toString());
  const isAllSelected = selectedFilters.length === 0;
  const isNoneSelected = selectedFilters.length === 1 && selectedFilters[0] === "none";

  return (
    <div className="px-2">
      <div className="flex items-center py-1 hover:cursor-pointer">
        <input type="checkbox" checked={isAllSelected} className="rounded border text-primaryBlue border-primaryBlue px-2 py-1 focus:ring-2 focus:ring-primaryBlue" onChange={handleAllClick} />
        <label className="ml-2 my-0">All [{allCount}]</label>
      </div>

      {supportList
        .slice() // copy before sorting
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(item => {
          const termId = item.term_id;
          const count = displayCounts[termId] || 0;

          if (!isAdvancedActive) {
            // No advanced search: show terms that appear in originalData at least once
            // Even if count=0 here, we still show them as required
            if (!originalTermIds.has(termId) && termId !== "none") {
              return null; // Term never appears in originalData
            }
          } else {
            // Advanced search active: show only terms with count > 0 from advancedOnlyData
            if (count === 0) {
              return null;
            }
          }

          return (
            <div key={item.term_id} className="flex items-center py-1 hover:cursor-pointer">
              <input type="checkbox" className="rounded border text-primaryBlue border-primaryBlue px-2 py-1 focus:ring-2 focus:ring-primaryBlue" id={`support-${item.term_id}`} name={item.name} value={item.term_id} checked={isChecked(item.term_id)} onChange={() => handleCheckboxChange(item.term_id)} />
              <label htmlFor={`support-${item.term_id}`} className="ml-2 my-0">
                {item.name} [{count}]
              </label>
            </div>
          );
        })}

      {noneCount > 0 && (
        <div className="flex items-center py-1 hover:cursor-pointer">
          <input type="checkbox" className="rounded border text-primaryBlue border-primaryBlue px-2 py-1 focus:ring-2 focus:ring-primaryBlue" checked={isNoneSelected} onChange={handleNoneClick} />
          <label className="ml-2 my-0">Not Specified [{noneCount}]</label>
        </div>
      )}
    </div>
  );
};

export default SupportFilterList;
